import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useMemo, useState, useEffect } from 'react';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useDeleteEvent, useGetEventList } from '../../queries/event';
import { formatDate, formatDateTime } from '../../utils/formatters/dateTimeFormat';
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const ViewEvents = () => {
  const navigate = useNavigate();
  const { data, isPending } = useGetEventList();
  const { mutateAsync: deleteEvent } = useDeleteEvent();
  const columnHelper = createColumnHelper();

  const [filteredData, setFilteredData] = useState([]);

  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    category: '',
    title: '',
    isHomePage: '',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = data;

    if (filters.fromDate) {
      filtered = filtered.filter((event) => new Date(event.startDate) >= new Date(filters.fromDate));
    }

    if (filters.toDate) {
      filtered = filtered.filter((event) => new Date(event.startDate) <= new Date(filters.toDate));
    }

    if (filters.status) {
      if (filters.status === 'active') {
        filtered = filtered.filter((event) => event.active === true);
      } else if (filters.status === 'inactive') {
        filtered = filtered.filter((event) => event.active === false);
      }
    }

    if (filters.category) {
      filtered = filtered.filter((event) => event?.typeOfEvent?.name === filters.category);
    }

    if(filters.isHomePage) {
      filtered = filtered.filter((event) => event.isHomePage === (filters.isHomePage === 'true'));
    }
    
    if (filters.title) {
      filtered = filtered.filter((event) =>
        event.title.toLowerCase().includes(filters.title.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  // Move the useMemo hook to the top level
  const categoryFilterOptions = useMemo(() => {
    if (!data) return [];
    const uniqueCategories = [...new Set(data.map(item => item.typeOfEvent?.name).filter(Boolean))];
    return uniqueCategories.map(category => ({
      value: category,
      label: category
    }));
  }, [data]);

  // Create columns definition at the top level
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr No.',
      cell: info => info.row.index + 1,
    }),
    columnHelper.accessor('typeOfEvent', {
      header: 'Category',
      cell: info => info.getValue()?.name || 'N/A',
      meta: {
        filterVariant: 'select',
        filterOptions: categoryFilterOptions
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        return row.original.typeOfEvent?.name === filterValue;
      }
    }),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: info => {
        const cityName = typeof info.row.original?.city === 'string'
          ? info.row.original.city
          : info.row.original?.city?.name || '';
        return (
          <span>
            {info.getValue() || 'N/A'}
            <br />
            <small className='text-secondary'>
              {cityName ? "(" + cityName + ")" : ''}
            </small>
          </span>
        );
      }
    }),
    columnHelper.accessor('eventOrganiser', {
      header: 'Organiser',
      cell: info => (
        <span>
          {info.getValue().ngoId ? info.getValue().ngoId.ngoName :
          info.getValue().companyId ? info.getValue().companyId.companyName : 'N/A'}
          <br />
          <small className='text-secondary'>
            {info.getValue().ngoId ? "(NGO)" :
            info.getValue().companyId ? "(Company)" : ''}
          </small>
        </span>
      ),
    }),
    columnHelper.accessor('points', {
      header: 'Points',
      align: 'center',
    }),
    columnHelper.accessor('participantCount', {
      header: 'Users',
      align: 'center',
      cell: info => (
        <span className='text-nowrap'>
          {info.row.original.submittedParticipantCount} Submitted
          <br />
          <small className='text-secondary'>
            ({info.getValue() || 0} Enrolled)
          </small>
        </span>
      )
    }),
    columnHelper.accessor('active', {
      header: 'Status',
      align: 'center',
      size: 10,
      cell: info => (<Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />),
    }),
    columnHelper.accessor('isHomePage', {
      header: 'Show in Home Page',
      align: 'center',
      size: 10,
      cell: info => (<Chip value={info.getValue() ? 'Yes' : 'No'} color={info.getValue() ? 'green' : 'inactive'} />),
    }),
    columnHelper.accessor('startDate', {
      header: 'Start Date',
      align: 'center',
      cell: info => (
        <div className='text-center'>
          {info.getValue().toRegularDate()}
          {info.row.original?.startTime && (
            <small className='text-xs text-secondary'>
              <br />
              ({info.row.original?.startTime})
            </small>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('endDate', {
      header: 'End Date',
      align: 'center',
      cell: info => (
        <div className='text-center'>
          {info.getValue().toRegularDate()}
        </div>
      ),
    }),
    // columnHelper.accessor('createdAt', {
    //   header: 'Created At',
    //   align: 'center',
    //   cell: info => (
    //     <div className='text-center'>
    //       {new Date(info.getValue()).toLocaleString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
    //       <br />
    //       {new Date(info.getValue()).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit' })}
    //     </div>
    //   ),
    // }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      align: 'center',
      enableColumnFilter: false,
      cell: info => {
        return (
          <div className='flex items-center justify-center'>
            <button
              className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
              onClick={() => navigate('/events/events-main/create-event', { state: { eventId: info.row.original?._id } })}
            >
              <img src={EditIcon} alt="view" />
            </button>
            <button
              className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
              onClick={() => {
                handleDelete(info.row.original._id);
                // setCurrentId(item._id);
              }}
            >
              <FaTrash className="fill-chipRed" />
            </button>
          </div>
        )
      },
    }),
  ], [categoryFilterOptions, navigate]);

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this event?");
    if(!confirm) return;
    await deleteEvent(id, {
      onSuccess: () => {
        toast.success("Event deleted successfully");
      }
    });
  }
  
  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  if (isPending) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
          <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
            <h1 className="self-start text-lg font-semibold">List of Events</h1>
            <p>Loading...</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      {/* Filters Section */}
      <form
        className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full"
        onSubmit={handleSearch}
      >
        <div className='flex items-center justify-between pr-5'>
          <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 w-5 aspect-square"
            />
            <span className="self-stretch">Filters</span>
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 aspect-square w-[18px]"
            />
          </div>
          <ActionButton
            variant={'reset'}
            text={'Create New Event'}
            handleSubmit={() => navigate('/events/events-main/create-event')}
          />
        </div>
        <div className="flex flex-wrap gap-4 p-5">
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                From Date
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                To Date
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="title" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Event Title
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  name="title"
                  value={filters.title}
                  onChange={handleFilterChange}
                  placeholder="Enter Event Title"
                  className='outline-none focus:ring-0 w-full'
                />
              </div>
            </div>
          </div>
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Status
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base w-full"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="isHomePage" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Show in Home Page
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="isHomePage"
                  value={filters.isHomePage}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base w-full"
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="category" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Event Category
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base w-full"
                >
                  <option value="">All</option>
                  {categoryFilterOptions.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className='flex gap-3 items-center justify-end p-5'>
          <ActionButton
            variant={'reset'}
            text={'Reset'}
            handleSubmit={() => {
              setFilters({
                fromDate: '',
                toDate: '',
                status: '',
                category: '',
                title: '',
                isHomePage: '',
              });
              setFilteredData(data);
            }}
          />
          <ActionButton
            variant={'submit'}
            text={'Search'}
            handleSubmit={() => { }}
          />
        </div>
      </form>

      {/* Events List Section */}
      <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
        <div className="flex flex-col pr-px w-full max-md:max-w-full">
          <h1 className="self-start text-lg font-bold pl-6 pb-4">List of Events</h1>
          <div className='px-6 text-base'>
            <TanStackTable
              data={filteredData}
              columns={columns}
              enablePagination={true}
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </main>
  )
}

export default ViewEvents;